import type {
    KanbanColumnConfig,
    TableColumnConfig,
} from "~/components/ottopilotDataViewer/columnConfiguration";
import type {FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID} from "~/components/ottopilotDataViewer/dataViewerDateRangeDefaults";
import type {Iso8601DateTime} from "@vesta/common-type-definitions/typeDefinitions";
import type {SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import type {JourneyScopeSelection} from "~/components/ottopilotDataViewer/journeyScopeFilterConfiguration";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

export type ConfigPageViewPreferencesV2 = {
    version: 2;
    configSignature: string;
    filterLineItems: Array<FilterConfigurationLineItem>;
    visibleFilterColumnIds: Array<string>;
    searchValue: string;
    sortLineItems: SortConfiguration["lineItems"];
    tableColumnConfig: {
        orderedVisibleColumnIds: Array<string>;
        pinnedLeftColumnIds: Array<string>;
    } | null;
    kanbanColumnConfig: {
        orderedColumnIds: Array<Uuid>;
        hiddenColumnIds: Array<Uuid>;
        pinnedColumnId: Uuid | null;
    } | null;
    /** Display-by selection when the page has journey scope and sidebar status nav is not active. */
    journeyScopeSelection?: JourneyScopeSelection;
    /** Created-at date range pill selection (stored separately from filterLineItems). */
    createdAtDateRange?: {
        from: Iso8601DateTime;
        to: Iso8601DateTime;
    } | null;
};

export function buildConfigPageViewPreferencesStorageKey(
    queryKeyPrefix: ReadonlyArray<string | null | undefined>,
): string {
    const segments = queryKeyPrefix.filter(
        (segment): segment is string => segment !== null && segment !== undefined && segment !== "",
    );
    return `ottopilot:config-page-view-prefs:v2:${segments.join(":")}`;
}

function isFilterLineItem(value: unknown): value is FilterConfigurationLineItem {
    if (value === null || typeof value !== "object") {
        return false;
    }
    const record = value as Record<string, unknown>;
    return typeof record.columnId === "string" && typeof record.operation === "string";
}

function parseJourneyScopeSelection(value: unknown): JourneyScopeSelection | null {
    if (value === "all" || value === "active" || value === "inactive") {
        return value;
    }
    return null;
}

function parseCreatedAtDateRange(value: unknown): ConfigPageViewPreferencesV2["createdAtDateRange"] {
    if (value === null) {
        return null;
    }
    if (value === undefined || typeof value !== "object" || Array.isArray(value)) {
        return undefined;
    }
    const record = value as Record<string, unknown>;
    if (typeof record.from !== "string" || typeof record.to !== "string") {
        return undefined;
    }
    return {
        from: record.from as Iso8601DateTime,
        to: record.to as Iso8601DateTime,
    };
}

function parseTableColumnConfig(value: unknown): ConfigPageViewPreferencesV2["tableColumnConfig"] {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }
    const record = value as Record<string, unknown>;
    const orderedVisibleColumnIds = Array.isArray(record.orderedVisibleColumnIds)
        ? record.orderedVisibleColumnIds.filter((id): id is string => typeof id === "string")
        : [];
    const pinnedLeftColumnIds = Array.isArray(record.pinnedLeftColumnIds)
        ? record.pinnedLeftColumnIds.filter((id): id is string => typeof id === "string")
        : [];
    if (orderedVisibleColumnIds.length === 0) {
        return null;
    }
    return {
        orderedVisibleColumnIds: orderedVisibleColumnIds,
        pinnedLeftColumnIds: pinnedLeftColumnIds,
    };
}

function parseKanbanColumnConfig(value: unknown): ConfigPageViewPreferencesV2["kanbanColumnConfig"] {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }
    const record = value as Record<string, unknown>;
    const orderedColumnIds = Array.isArray(record.orderedColumnIds)
        ? record.orderedColumnIds.filter((id): id is Uuid => typeof id === "string")
        : [];
    const hiddenColumnIds = Array.isArray(record.hiddenColumnIds)
        ? record.hiddenColumnIds.filter((id): id is Uuid => typeof id === "string")
        : [];
    const pinnedColumnId = typeof record.pinnedColumnId === "string" ? (record.pinnedColumnId as Uuid) : null;
    if (
        orderedColumnIds.length === 0 &&
        hiddenColumnIds.length === 0 &&
        pinnedColumnId === null
    ) {
        return null;
    }
    return {
        orderedColumnIds: orderedColumnIds,
        hiddenColumnIds: hiddenColumnIds,
        pinnedColumnId: pinnedColumnId,
    };
}

export function loadConfigPageViewPreferences(
    storageKey: string,
    expectedConfigSignature: string,
): ConfigPageViewPreferencesV2 | null {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (raw === null || raw === "") {
            return null;
        }
        const parsed: unknown = JSON.parse(raw);
        if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
            clearConfigPageViewPreferences(storageKey);
            return null;
        }
        const record = parsed as Record<string, unknown>;
        if (record.version !== 2) {
            clearConfigPageViewPreferences(storageKey);
            return null;
        }
        if (
            typeof record.configSignature !== "string" ||
            record.configSignature !== expectedConfigSignature
        ) {
            clearConfigPageViewPreferences(storageKey);
            return null;
        }
        const filterLineItems = Array.isArray(record.filterLineItems)
            ? record.filterLineItems.filter(isFilterLineItem)
            : [];
        const visibleFilterColumnIds = Array.isArray(record.visibleFilterColumnIds)
            ? record.visibleFilterColumnIds.filter((id): id is string => typeof id === "string")
            : [];
        const searchValue = typeof record.searchValue === "string" ? record.searchValue : "";
        const sortLineItems = Array.isArray(record.sortLineItems) ? record.sortLineItems : [];
        const tableColumnConfig = parseTableColumnConfig(record.tableColumnConfig);
        const kanbanColumnConfig = parseKanbanColumnConfig(record.kanbanColumnConfig);
        const journeyScopeSelection =
            typeof record.journeyScopeSelection === "string"
                ? parseJourneyScopeSelection(record.journeyScopeSelection) ?? undefined
                : undefined;
        const createdAtDateRange = parseCreatedAtDateRange(record.createdAtDateRange);
        return {
            version: 2,
            configSignature: record.configSignature,
            filterLineItems: filterLineItems,
            visibleFilterColumnIds: visibleFilterColumnIds,
            searchValue: searchValue,
            sortLineItems: sortLineItems,
            tableColumnConfig: tableColumnConfig,
            kanbanColumnConfig: kanbanColumnConfig,
            journeyScopeSelection: journeyScopeSelection,
            createdAtDateRange: createdAtDateRange,
        };
    } catch {
        clearConfigPageViewPreferences(storageKey);
        return null;
    }
}

export function saveConfigPageViewPreferences(
    storageKey: string,
    state: ConfigPageViewPreferencesV2,
    dateRangeColumnId: string = DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
): void {
    if (typeof window === "undefined") {
        return;
    }
    try {
        const sanitizedState: ConfigPageViewPreferencesV2 = {
            ...state,
            visibleFilterColumnIds: state.visibleFilterColumnIds.filter(
                (columnId) => columnId !== dateRangeColumnId,
            ),
        };
        window.localStorage.setItem(storageKey, JSON.stringify(sanitizedState));
    } catch {
        // quota / private mode
    }
}

export function clearConfigPageViewPreferences(storageKey: string): void {
    if (typeof window === "undefined") {
        return;
    }
    try {
        window.localStorage.removeItem(storageKey);
    } catch {
        // ignore
    }
}

export function toTableColumnConfigFromPreferences(
    tableColumnConfig: ConfigPageViewPreferencesV2["tableColumnConfig"],
): TableColumnConfig | null {
    if (tableColumnConfig === null) {
        return null;
    }
    return {
        revisionId: generateUuid(),
        orderedVisibleColumnIds: tableColumnConfig.orderedVisibleColumnIds,
        pinnedLeftColumnIds: tableColumnConfig.pinnedLeftColumnIds,
    };
}

export function toKanbanColumnConfigFromPreferences(
    kanbanColumnConfig: ConfigPageViewPreferencesV2["kanbanColumnConfig"],
): KanbanColumnConfig | null {
    if (kanbanColumnConfig === null) {
        return null;
    }
    return {
        revisionId: generateUuid(),
        orderedColumnIds: kanbanColumnConfig.orderedColumnIds,
        hiddenColumnIds: kanbanColumnConfig.hiddenColumnIds,
        pinnedColumnId: kanbanColumnConfig.pinnedColumnId,
    };
}

export function buildConfigPageViewPreferencesFromState({
    configSignature,
    filterLineItems,
    visibleFilterColumnIds,
    searchValue,
    sortLineItems,
    tableColumnConfig,
    kanbanColumnConfig,
    journeyScopeSelection,
    createdAtDateRange,
}: {
    configSignature: string;
    filterLineItems: Array<FilterConfigurationLineItem>;
    visibleFilterColumnIds: Array<string>;
    searchValue: string;
    sortLineItems: SortConfiguration["lineItems"];
    tableColumnConfig: TableColumnConfig | null;
    kanbanColumnConfig: KanbanColumnConfig | null;
    journeyScopeSelection?: JourneyScopeSelection;
    createdAtDateRange?: ConfigPageViewPreferencesV2["createdAtDateRange"];
}): ConfigPageViewPreferencesV2 {
    return {
        version: 2,
        configSignature: configSignature,
        filterLineItems: filterLineItems,
        visibleFilterColumnIds: visibleFilterColumnIds,
        searchValue: searchValue,
        sortLineItems: sortLineItems,
        tableColumnConfig:
            tableColumnConfig === null
                ? null
                : {
                      orderedVisibleColumnIds: tableColumnConfig.orderedVisibleColumnIds,
                      pinnedLeftColumnIds: tableColumnConfig.pinnedLeftColumnIds,
                  },
        kanbanColumnConfig:
            kanbanColumnConfig === null
                ? null
                : {
                      orderedColumnIds: kanbanColumnConfig.orderedColumnIds,
                      hiddenColumnIds: kanbanColumnConfig.hiddenColumnIds,
                      pinnedColumnId: kanbanColumnConfig.pinnedColumnId,
                  },
        ...(journeyScopeSelection !== undefined ? {journeyScopeSelection: journeyScopeSelection} : {}),
        ...(createdAtDateRange !== undefined ? {createdAtDateRange: createdAtDateRange} : {}),
    };
}
